import { Link } from "react-router-dom";
import { ArrowLeft, Eye, PenLine, ExternalLink, Mail } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/api";
import { ScheduleButton } from "@/pages/admin/ScheduleButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const btn = "inline-flex items-center gap-2 font-ui text-sm px-4 py-2.5 border transition-colors disabled:opacity-50";
const ghost = `${btn} border-[#CFCFCB] bg-white hover:border-[var(--ink)]`;
const solid = `${btn} border-[var(--ink)] bg-[var(--ink)] text-white hover:bg-[var(--accent)] hover:border-[var(--accent)]`;

const SendButton = ({ saved, busy, onSend }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} className={ghost} data-testid="admin-send-subscribers-button">
        <Mail size={15} /> {saved.newsletter_sent_at ? "Send again" : "Send to subscribers"}
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="rounded-none">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-2xl">Email this article to subscribers?</AlertDialogTitle>
        <AlertDialogDescription data-testid="admin-send-dialog-description">
          Every confirmed subscriber will get an email with the headline, standfirst and a link to read it.
          {saved.newsletter_sent_at && ` This article was last sent on ${formatDate(saved.newsletter_sent_at)}.`}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-none" data-testid="admin-send-cancel-button">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onSend} className="rounded-none bg-[var(--ink)] hover:bg-[var(--accent)]" data-testid="admin-send-confirm-button">Send now</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const EditorActions = ({ isNew, saved, busy, preview, setPreview, onSave, onDelete, onSend }) => {
  const published = saved?.status === "published";
  return (
    <div className="flex flex-wrap items-center gap-3 mb-8">
      <Link to="/admin" className="inline-flex items-center gap-1.5 font-ui text-sm text-[var(--ink-2)] hover:text-[var(--ink)] mr-auto" data-testid="admin-back-link">
        <ArrowLeft size={15} /> All articles
        {saved && (
          <span className="ml-3 meta" data-testid="admin-editor-status">
            {saved.status === "scheduled" ? `Scheduled · ${formatDateTime(saved.published_at)}` : saved.status}
          </span>
        )}
      </Link>
      {published && <SendButton saved={saved} busy={busy} onSend={onSend} />}
      {published && (
        <a href={`/article/${saved.slug}`} target="_blank" rel="noreferrer" className={ghost} data-testid="admin-view-live-link"><ExternalLink size={15} /> View live</a>
      )}
      <button type="button" onClick={() => setPreview(!preview)} className={ghost} data-testid="admin-preview-toggle">
        {preview ? <><PenLine size={15} /> Edit</> : <><Eye size={15} /> Preview</>}
      </button>
      {published || saved?.status === "scheduled" ? (
        <button disabled={busy} onClick={() => onSave("draft")} className={ghost} data-testid="admin-unpublish-button">
          {published ? "Unpublish" : "Cancel schedule"}
        </button>
      ) : (
        <button disabled={busy} onClick={() => onSave("draft")} className={ghost} data-testid="admin-save-draft-button">Save draft</button>
      )}
      {!published && <ScheduleButton saved={saved} busy={busy} onSave={onSave} />}
      <button disabled={busy} onClick={() => onSave("published")} className={solid} data-testid="admin-save-article-button">
        {published ? "Update" : "Publish now"}
      </button>
      {!isNew && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className={`${btn} border-transparent text-[var(--accent)] hover:border-[var(--accent)]`} data-testid="admin-delete-article-button">Delete</button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl">Delete this article?</AlertDialogTitle>
              <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="admin-delete-cancel-button">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="rounded-none bg-[var(--accent)] hover:bg-[var(--accent-hover)]" data-testid="admin-delete-confirm-button">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};
